
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChatMessage } from "@/components/notifications/chat/types";
import { setupChatRealtime } from "./useChatRealtime";
import { handleChatError } from "@/utils/chatErrorHandler";

export const useChatMessages = (notificationId: string | undefined) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { user } = useAuth();

  // Function to update read status - declare early so it can be used in useEffect
  const updateReadStatus = useCallback(async () => {
    if (!user?.id || !notificationId) return;
    
    try {
      const { error } = await supabase.rpc(
        'update_notification_chat_read_status',
        { 
          p_notification_id: notificationId,
          p_user_id: user.id
        }
      );
      
      if (error) {
        console.error("Error updating read status:", error);
      } else {
        console.log("Read status updated successfully");
      }
    } catch (error) {
      console.error("Error updating read status:", error);
    }
  }, [user?.id, notificationId]);

  useEffect(() => {
    if (!notificationId) return;
    
    const fetchMessages = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_chat')
        .select('*')
        .eq('notification_id', notificationId)
        .order('created_at', { ascending: true });
      
      if (error) {
        handleChatError(error, 'load');
      } else {
        setMessages(data as ChatMessage[]);
      }
      setLoading(false);
    };

    fetchMessages();
    
    // Mark messages as read when opening notification
    if (user?.id) {
      setTimeout(() => updateReadStatus(), 100); // Small delay to ensure messages are loaded
    }
    
    // Set up realtime subscription using the utility function
    const cleanup = setupChatRealtime(
      notificationId,
      // onMessageInsert
      (payload) => {
        setMessages(prev => [...prev, payload.new as ChatMessage]);
        
        // Update read status for new messages from others
        if (user?.id && payload.new.user_id !== user.id) {
          setTimeout(() => updateReadStatus(), 500);
        }
      },
      // onMessageUpdate
      (payload) => {
        setMessages(prev => 
          prev.map(msg => msg.id === payload.new.id ? payload.new as ChatMessage : msg)
        );
      }
    );

    // Return cleanup function
    return cleanup;
  }, [notificationId, user?.id, updateReadStatus]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !user || !notificationId) return;

    try {
      // Get the current session user ID to ensure RLS policy compliance
      const { data: sessionData, error: sessionError } = await supabase
        .rpc('get_current_session_user_id');
      
      if (sessionError) {
        console.error("Error getting session user ID:", sessionError);
        handleChatError(sessionError, 'send');
        return;
      }
      
      // Use session user ID, fall back to auth user ID
      const userIdForInsert = sessionData || user.id;
      
      if (!userIdForInsert) {
        handleChatError(new Error("Geen geldige gebruikers ID gevonden"), 'send');
        return;
      }
      
      const { data, error } = await supabase
        .from('notification_chat')
        .insert({
          notification_id: notificationId,
          user_id: userIdForInsert,
          user_name: user.name || 'Anonymous',
          message: newMessage.trim()
        })
        .select('*')
        .single();

      if (error) {
        console.error("Error sending message:", error);
        throw error;
      }
      
      
      setNewMessage("");
      setShowEmojiPicker(false);
      
      // Update read status after sending a message
      setTimeout(() => updateReadStatus(), 100);
    } catch (error: any) {
      handleChatError(error, 'send');
    }
  };

  const addEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const editMessage = async (messageId: string, text: string) => {
    if (!text.trim() || !user) return;

    try {
      const { error } = await supabase
        .from('notification_chat')
        .update({
          message: text.trim(),
          updated_at: new Date().toISOString(),
          is_edited: true
        })
        .eq('id', messageId);

      if (error) throw error;
      
      setEditingMessage(null);
      setEditText("");
    } catch (error: any) {
      handleChatError(error, 'edit');
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!user) return;
    
    if (!confirm("Weet je zeker dat je dit bericht wilt verwijderen?")) return;

    try {
      const { error } = await supabase
        .from('notification_chat')
        .update({
          is_deleted: true,
          message: "[Dit bericht is verwijderd]",
          updated_at: new Date().toISOString()
        })
        .eq('id', messageId);

      if (error) throw error;
    } catch (error: any) {
      handleChatError(error, 'delete');
    }
  };

  return {
    messages,
    loading,
    newMessage,
    setNewMessage,
    editingMessage,
    setEditingMessage,
    editText,
    setEditText,
    sendMessage,
    editMessage,
    deleteMessage,
    showEmojiPicker,
    setShowEmojiPicker,
    addEmoji,
    updateReadStatus
  };
};
